import { CreateBase, Form, useNotify, useRedirect, useTranslate, type Identifier } from "ra-core";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRoleDataScope } from "../commons/useRoleDataScope";

import type { Contact } from "../types";
import { JornadaDoFormulario } from "../commons/JornadaDoFormulario";
import { RastrearAlteracoes, useSairDoCadastro } from "../commons/SairDoCadastro";
import { passosDaFicha } from "./passosDaFicha";
import { mensagemDeErroAoGravarContato, normalizarDetalhesDoContato } from "./gravarContato";
import { SalvarComResponsavel, useTrocaAoSalvar } from "../crm/responsavel";
import { ConferirCelularAoSalvar, useCelularDuplicado } from "./CelularDuplicado";

interface ContactCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (contact: Contact) => void;
  showLeadDialog?: boolean;
  /**
   * Mostra o campo "Responsável". Só no cadastro do CRM: aberto de uma venda,
   * simulação ou cotação, o contato criado é selecionado ali mesmo por quem
   * está vendendo, e mandá-lo para outra pessoa quebraria esse fluxo.
   */
  comResponsavel?: boolean;
  /** Aberto pela rota da página: o voltar do navegador já troca a rota. */
  daRota?: boolean;
}

export const ContactCreateDialog = ({
  open,
  onClose,
  onSuccess,
  showLeadDialog = true,
  comResponsavel = false,
  daRota = false,
}: ContactCreateDialogProps) => {
  const { currentCrmUserId } = useRoleDataScope();
  const currentSalesId = currentCrmUserId;
  const [showLeadPrompt, setShowLeadPrompt] = useState(false);
  const [createdContactId, setCreatedContactId] = useState<Identifier | null>(null);
  const redirect = useRedirect();
  const translate = useTranslate();
  const notify = useNotify();
  const { tentarSair, aviso, aoAlterar } = useSairDoCadastro(onClose);
  const { separar, aplicar } = useTrocaAoSalvar();
  // Celular na base de outra pessoa: modal com "Avisar o gestor" (migration 241).
  const celular = useCelularDuplicado("cadastro");

  const handleSuccess = async (contact: Contact) => {
    // Criado por quem cadastra; foi para outra pessoa, não há mais o que
    // oferecer aqui (nem lead, nem abrir a ficha, que talvez não carregue).
    if (await aplicar({ contactId: contact.id })) {
      onClose();
      return;
    }
    onSuccess?.(contact);
    if (showLeadDialog) {
      setCreatedContactId(contact.id);
      setShowLeadPrompt(true);
      return;
    }

    onClose();
  };

  const handleCreateLead = () => {
    setShowLeadPrompt(false);
    redirect(`/deals/create?contact_id=${createdContactId}`);
  };

  const handleSkip = () => {
    setShowLeadPrompt(false);
    if (createdContactId) {
      redirect("show", "contacts", createdContactId);
    }
  };

  return (
    <>
      <Dialog open={open && !showLeadPrompt} onOpenChange={(isOpen) => !isOpen && tentarSair()} voltarFecha={!daRota}>
        <DialogContent
          aria-describedby={undefined}
          // Painel lateral à direita a partir do tablet (Premium North): mesma janela,
          // mesmos passos e o mesmo "voltar fecha"; no celular continua centralizada.
          className="z-[60] max-w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto content-start sm:left-auto sm:right-0 sm:top-0 sm:h-[100dvh] sm:max-h-[100dvh] sm:w-[640px] sm:max-w-[640px] sm:translate-x-0 sm:translate-y-0 sm:rounded-none sm:rounded-l-[14px] sm:border-y-0 sm:border-r-0 sm:shadow-[-16px_0_40px_-12px_rgba(16,24,40,0.25)] sm:data-[state=open]:zoom-in-100 sm:data-[state=closed]:zoom-out-100 sm:data-[state=open]:slide-in-from-right-8">
          <DialogHeader>
            <DialogTitle>{translate("atomic.contacts.createContact")}</DialogTitle>
          </DialogHeader>
          <CreateBase
            resource="contacts"
            redirect={false}
            mutationOptions={{
              onSuccess: handleSuccess,
              onError: (error: unknown) => {
                if (celular.aoErro(error)) return;
                notify(mensagemDeErroAoGravarContato(error), { type: 'error' });
              },
            }}
            record={{
              user_id: currentSalesId,
            }}
            transform={(comResponsavel: Contact) => {
              const data = separar(comResponsavel, currentSalesId);
              celular.lembrar(data.details as Record<string, unknown>);
              const transformed = {
                ...data,
                details: normalizarDetalhesDoContato(data.details as Record<string, unknown>),
                first_seen: new Date().toISOString(),
                last_seen: new Date().toISOString(),
                tags: [],
              };

              // Only set user_id if we have a valid value
              if (data.user_id) {
                transformed.user_id = data.user_id;
              } else if (currentSalesId) {
                transformed.user_id = currentSalesId;
              }

              return transformed;
            }}
          >
            <Form>
              <RastrearAlteracoes aoMudar={aoAlterar} />
              {/* Cancelar fecha pelo onClose: o CancelButton padrão faz
                  navigate(-1), que dentro de um diálogo tira a pessoa da
                  página em que estava (do fluxo de venda, por exemplo). */}
              <JornadaDoFormulario
                passos={passosDaFicha(comResponsavel ? { responsavel: { donoAtual: currentSalesId } } : {})}
                acaoSecundaria={
                  <Button type="button" variant="ghost" onClick={onClose}>
                    {translate("ra.action.cancel", { _: "Cancelar" })}
                  </Button>
                }
                acaoFinal={
                  <ConferirCelularAoSalvar aoDuplicadoDeOutro={celular.abrir}>
                    <SalvarComResponsavel tipo="contato" donoAtual={currentSalesId} contatoIds={[]} />
                  </ConferirCelularAoSalvar>
                }
              />
            </Form>
          </CreateBase>
        </DialogContent>
      </Dialog>
      {aviso}
      {celular.modal}

      {showLeadDialog && (
        <Dialog open={showLeadPrompt} onOpenChange={(isOpen) => !isOpen && handleSkip()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{translate("atomic.contacts.createLeadTitle")}</DialogTitle>
              <DialogDescription>
                {translate("atomic.contacts.createLeadDescription")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleSkip}>
                {translate("atomic.contacts.skipDeal")}
              </Button>
              <Button onClick={handleCreateLead}>
                {translate("atomic.deals.createDeal")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const ContactCreate = () => {
  const redirect = useRedirect();

  return (
    <ContactCreateDialog
      open
      onClose={() => redirect("list", "contacts")}
      showLeadDialog={false}
      comResponsavel
      daRota
    />
  );
};
